<script setup lang="ts">
import { computed, ref, watch } from 'vue'
const props = withDefaults(
    defineProps<{
        fieldName: string
        fields: number
        inputMode?: 'text' | 'search' | 'none' | 'tel' | 'url' | 'email' | 'numeric' | 'decimal' | undefined
        specialGapsAfter?: number[]
        error?: boolean | string
        required?: boolean
    }>(),
    {
        specialGapsAfter: () => [],
        inputMode: 'text',
        required: false
    },
)
const data = ref<string[]>([])
const firstInputEl = ref<HTMLInputElement[] | null>(null)
const inputsWrapEl = ref<HTMLDivElement | null>(null)
const emit = defineEmits(['update:modelValue', 'update:validateValue', 'invalid'])

const fieldsGroups = computed(() => {
    let groups: any[] = []
    let groupIndex = 0
    for (let i = 1; i <= props.fields; i++) {
        if (groups[groupIndex]) {
            groups[groupIndex].push(i)
        } else {
            groups[groupIndex] = [i]
        }
        if (props.specialGapsAfter.includes(i)) {
            groupIndex++
        }
    }
    return groups
})

watch(
    data,
    (newVal) => {
        if (
            newVal.length === props.fields &&
            newVal.every(Boolean)
        ) {
            emit('update:modelValue', newVal.join(''))
            emit('update:validateValue', {
                value: newVal.join(''),
                field_name: props.fieldName,
            })
        } else {
            emit('update:modelValue', null)
        }
    },
    { deep: true },
)

const handleOtpInput = (e: InputEvent) => {
    const target = e.target as HTMLInputElement | null
    if (!target) {
        return
    }
    const inputs = inputsWrapEl.value?.querySelectorAll('input')
    if (!inputs?.length) {
        return
    }
    const index = Array.from(inputs).indexOf(target)
    if (index < 0) {
        return
    }
    if (e.data && inputs[index + 1]) {
        (inputs[index + 1] as HTMLInputElement).focus()
    } else if (e.data == null && target.previousElementSibling) {
        const prev = inputs[index - 1] as HTMLInputElement | undefined
        prev?.focus()
    }
}

const handleKeydown = (e: KeyboardEvent) => {
    if (e.key !== 'Backspace') {
        return
    }
    const target = e.target as HTMLInputElement | null
    if (!target || target.value) {
        return
    }
    const inputs = inputsWrapEl.value?.querySelectorAll('input')
    if (!inputs?.length) {
        return
    }
    const index = Array.from(inputs).indexOf(target)
    if (index > 0) {
        ;(inputs[index - 1] as HTMLInputElement).focus()
    }
}

const handlePaste = (e: ClipboardEvent) => {
    const pasteData = e.clipboardData?.getData('text') ?? ''
    const chars = pasteData.slice(0, props.fields).split('')
    if (!chars.length) {
        return
    }
    e.preventDefault()
    chars.forEach((char, index) => {
        data.value[index] = char
    })
    const lastIndex = chars.length - 1
    const lastEl = firstInputEl.value?.[lastIndex]
    lastEl?.focus()
}
</script>

<template>
    <div
        ref="inputsWrapEl"
        class="otp w-full flex flex-wrap justify-start gap-4"
        @input="handleOtpInput"
    >
        <div v-for="fieldGroup in fieldsGroups" class="flex gap-1.5">
            <template v-for="field in fieldGroup" :key="field">
                <input
                    v-model="data[field - 1]"
                    ref="firstInputEl"
                    maxlength="1"
                    :inputmode="inputMode"
                    :required="required"
                    class="w-[37px] h-[56px] text-center"
                    :class="{'input-error': error}"
                    placeholder="_"
                    @paste="field === 1 && handlePaste($event)"
                    @keydown="handleKeydown"
                    @invalid.prevent="emit('invalid', $event)"
                />
            </template>
        </div>
    </div>
</template>

<style scoped>
input {
    background-color: var(--color-default-white);
    color: var(--color-primary);
}
</style>
